import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  ClickAwayListener,
  Portal,
  Drawer,
  makeStyles,
} from '@material-ui/core';
import { MonitorIcon, SmartphoneIcon, XIcon } from './adsAgentIcons';
import { AD } from './adsAgentTokens';

// Port of whatsapp-console-frontend `src/components/AspectRatioSelector.jsx`.
const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    display: 'block',
    width: '100%',
  },
  dropdown: {
    position: 'fixed',
    backgroundColor: AD.surface,
    border: `1px solid ${AD.divider}`,
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 999999,
    width: '520px',
    maxHeight: '420px',
    overflow: 'hidden',
    fontFamily: AD.fontFamily,
  },
  dropdownContent: {
    padding: '24px',
    display: 'flex',
    gap: '32px',
    [theme.breakpoints.down('sm')]: {
      padding: '20px 16px',
      gap: '16px',
      flexDirection: 'column',
      height: '100%',
      overflow: 'auto',
      justifyContent: 'flex-start',
    },
  },
  leftSection: {
    flex: '0 0 200px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
      flex: 'none',
      width: '100%',
      gap: '12px',
      alignItems: 'center',
    },
  },
  rightSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      gap: '8px',
    },
  },
  title: {
    fontFamily: AD.fontFamily,
    fontSize: '16px',
    fontWeight: 600,
    color: AD.text,
    marginBottom: '16px',
  },
  previewBox: {
    width: '120px',
    height: '90px',
    border: `1px solid ${AD.divider}`,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F6F7FF',
    margin: '0 auto',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      width: '150px',
      height: '110px',
    },
  },
  previewRatio: {
    position: 'absolute',
    border: `1px solid ${AD.primary}`,
    backgroundColor: `${AD.primarySoft}20`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  previewText: {
    fontFamily: AD.fontFamily,
    fontSize: '14px',
    color: AD.text,
    fontWeight: 500,
  },
  dimensionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'center',
  },
  dimensionText: {
    fontFamily: AD.fontFamily,
    fontSize: '12px',
    color: AD.textMuted,
  },
  ratiosContainer: {
    display: 'flex',
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
      gap: '8px',
      flexDirection: 'column',
    },
  },
  ratioColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    [theme.breakpoints.down('sm')]: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
    },
  },
  columnTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontSize: '12px',
    fontWeight: 600,
    color: AD.text,
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
      fontSize: '14px',
      marginBottom: '12px',
      fontWeight: 600,
    },
  },
  ratioOption: {
    padding: '8px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    color: AD.text,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&.selected': {
      backgroundColor: AD.primary,
      color: 'white',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '12px 16px',
      fontSize: '14px',
      minHeight: '48px',
      borderRadius: '8px',
      fontWeight: 500,
      marginBottom: '6px',
    },
  },
  additionalOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '8px',
    [theme.breakpoints.down('sm')]: {
      gap: '6px',
      marginTop: '12px',
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${AD.divider}`,
    backgroundColor: AD.surface,
  },
  drawerTitle: {
    fontFamily: AD.fontFamily,
    fontSize: 12,
    fontWeight: 600,
  },
  drawerClose: {
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

type Ratio = { value: string; label: string };

const aspectRatios: Record<'portrait' | 'landscape' | 'additional', Ratio[]> = {
  portrait: [
    { value: '9:16', label: '9:16' },
    { value: '2:3', label: '2:3' },
    { value: '3:4', label: '3:4' },
  ],
  landscape: [
    { value: '16:9', label: '16:9' },
    { value: '3:2', label: '3:2' },
    { value: '4:3', label: '4:3' },
  ],
  additional: [{ value: '1:1', label: '1:1 (Square)' }],
};

const ratioParts = (ratio: string): [number, number] => {
  const [width, height] = ratio.split(':').map(Number);
  return [width, height];
};

const AspectRatioSelector = (props: Props): React.ReactElement => {
  const { currentRatio, onRatioChange, children, previewSize = 60 } = props;
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRatio, setSelectedRatio] = useState(currentRatio || '4:5');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedRatio(currentRatio);
  }, [currentRatio]);

  const handleRatioSelect = (ratio: Ratio) => {
    setSelectedRatio(ratio.value);
    onRatioChange(ratio.value);
    setIsOpen(false);
  };

  const calculateDropdownPosition = () => {
    if (!containerRef.current) {
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const dropdownWidth = 520;
    let left = rect.left;
    const maxLeft = window.innerWidth - dropdownWidth;
    if (left > maxLeft) {
      left = maxLeft;
    }
    setDropdownPosition({ top: rect.bottom + 4, left });
  };

  const handleClick = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      calculateDropdownPosition();
      setIsOpen(true);
    }
  };

  const isMobile = window.innerWidth < 600;

  // Close the dropdown on scroll (desktop only).
  useEffect(() => {
    if (!isOpen || isMobile) {
      return undefined;
    }
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, isMobile]);

  const [ratioW, ratioH] = ratioParts(selectedRatio);
  const ratio = ratioW / ratioH;
  const previewDims =
    ratio > 1
      ? { width: previewSize, height: previewSize / ratio }
      : { width: previewSize * ratio, height: previewSize };
  const actualDims =
    ratio > 1
      ? { width: 1200, height: Math.round(1200 / ratio) }
      : { width: Math.round(1200 * ratio), height: 1200 };

  const renderOption = (option: Ratio) => (
    <Box
      key={option.value}
      className={`${classes.ratioOption} ${
        selectedRatio === option.value ? 'selected' : ''
      }`}
      onMouseEnter={() => setSelectedRatio(option.value)}
      onClick={() => handleRatioSelect(option)}
    >
      {option.label}
    </Box>
  );

  const renderContent = () => (
    <Box className={classes.dropdownContent}>
      <Box className={classes.leftSection}>
        <Typography className={classes.title}>Aspect ratio</Typography>
        <Box className={classes.previewBox}>
          <Box
            className={classes.previewRatio}
            style={{
              width: previewDims.width,
              height: previewDims.height,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <Typography className={classes.previewText}>
              {selectedRatio}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.dimensionsContainer}>
          <Typography className={classes.dimensionText}>
            Width {actualDims.width} px
          </Typography>
          <Typography className={classes.dimensionText}>
            Height {actualDims.height} px
          </Typography>
        </Box>
      </Box>

      <Box className={classes.rightSection}>
        <Box className={classes.ratiosContainer}>
          <Box className={classes.ratioColumn}>
            <Box className={classes.columnTitle}>
              <SmartphoneIcon size={16} color={AD.textMuted} />
              Portrait
            </Box>
            {aspectRatios.portrait.map(renderOption)}
          </Box>
          <Box className={classes.ratioColumn}>
            <Box className={classes.columnTitle}>
              <MonitorIcon size={16} color={AD.textMuted} />
              Landscape
            </Box>
            {aspectRatios.landscape.map(renderOption)}
          </Box>
        </Box>
        <Box className={classes.additionalOptions}>
          {aspectRatios.additional.map(renderOption)}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <div ref={containerRef} className={classes.container} onClick={handleClick}>
        {children}
      </div>

      {isMobile ? (
        <Drawer
          anchor="right"
          open={isOpen}
          onClose={() => setIsOpen(false)}
          PaperProps={{
            style: {
              width: '100vw',
              maxWidth: '100vw',
              backgroundColor: '#ffffff',
              boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          <Box display="flex" flexDirection="column" height="100%">
            <Box className={classes.drawerHeader}>
              <Typography className={classes.drawerTitle}>
                Select Aspect Ratio
              </Typography>
              <Box
                className={classes.drawerClose}
                onClick={() => setIsOpen(false)}
              >
                <XIcon size={24} color="#666" />
              </Box>
            </Box>
            <Box flex={1} overflow="auto">
              {renderContent()}
            </Box>
          </Box>
        </Drawer>
      ) : (
        isOpen && (
          <Portal>
            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
              <Box
                className={classes.dropdown}
                style={{
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
              >
                {renderContent()}
              </Box>
            </ClickAwayListener>
          </Portal>
        )
      )}
    </>
  );
};

interface Props {
  currentRatio: string;
  onRatioChange: (value: string) => void;
  children: React.ReactNode;
  previewSize?: number;
}

export default AspectRatioSelector;
